import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppLoader from '@nxsys/components/AppLoader';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useDownloadPayslipEmployee } from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetPayslipEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';
import { showMessage } from '@nxsys/utils/message';
import { FilterOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { getPayslipColumns, withStatusColumn } from './columns';
import PayslipFilterModal from './PayslipFilterModal';

const PayslipListing = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigateWithQuery();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<GetPayslipEmployeeListParams>
  >({});

  const { user, payrollCountry, permissions, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { payslipList } = useSelector((state: AppState) => state.employeeStore);
  const { loading, list, pagination, queryParams } = payslipList;

  const subcontractorId = user?.subcontractorId?.id ?? '';

  const { run: downloadPayslipEmployee, loading: isDownloadPayslipPending } =
    useDownloadPayslipEmployee();

  const paymentModel =
    user?.subcontractorId?.setting?.paymentModel ||
    user?.subcontractorId?.paymentModel;

  const payFrequency = user?.subcontractorId?.payFrequency || 'WEEKLY';

  const handleFetchData = useCallback(
    (params: GetPayslipEmployeeListParams) => {
      if (!subcontractorId) return;
      dispatch(
        employeeActions.getPayslipEmployeeList({
          ...params,
          id: subcontractorId,
        }),
      );
    },
    [dispatch, subcontractorId],
  );

  useEffect(() => {
    if (subcontractorId) {
      handleFetchData({
        page: 0,
        size: 20,
      });
    }
  }, [subcontractorId]);

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleFetchData({
        ...queryParams,
        page: 0,
        size: 20,
        search: search || undefined,
      });
    }, 500),
    [queryParams, handleFetchData],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field || undefined,
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedFetchData(e.target.value);
  };

  const handleFilter = (params: Partial<GetPayslipEmployeeListParams>) => {
    setAppliedFilters(params);
    handleFetchData({
      ...queryParams,
      ...params,
      page: 0,
      size: queryParams?.size || 20,
    });
  };

  const handleDownload = async (id: string) => {
    try {
      const res = await downloadPayslipEmployee(id);
      if (res?.data) {
        window.open(res.data, '_blank');
      } else {
        // NX-5866: see Payslip.tsx — a refusal arrives as HTTP 200.
        showMessage({
          type: 'error',
          content: res?.error
            ? getErrorMessage(res.error)
            : messages['message.somethingWentWrong'],
        });
      }
    } catch {
      // Error handled by hook
    }
  };

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`/payslips/${id}`);
    },
    [navigate],
  );

  const columns = withStatusColumn(
    getPayslipColumns(
      {
        payrollCountry,
        permissions: permissions ?? [],
        paymentModel,
        onDownload: handleDownload,
        onViewDetail: handleViewDetail,
        messages,
      },
      isBureau,
    ),
    messages,
  );

  const isCIS = isCISPaymentModel(paymentModel);

  const pageTitle = isCIS
    ? messages['common.selfBill']
    : messages['common.payslip'];

  const renderTitle = useCallback(
    () => <AppPageTitle prefix={pageTitle} separator="" title="" />,
    [pageTitle],
  );

  if (!subcontractorId) {
    return (
      <AppsContainer fullView type="bottom" title={renderTitle()}>
        <AppCard>
          <AppsContent>
            <AppTableContainer
              data={[]}
              loading={false}
              page={0}
              total={0}
              pageSize={20}
              titleEmpty={messages['payslip.empty'] ?? 'No payslips available.'}
              columns={[]}
              pagination={false}
            />
          </AppsContent>
        </AppCard>
      </AppsContainer>
    );
  }

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      loading={isDownloadPayslipPending}
    >
      {isDownloadPayslipPending && <AppLoader />}
      <AppCard>
        <div className="p-[16px]">
          <div className="flex justify-between items-center mb-[16px]">
            <AppInputSearch onChange={handleSearchChange} />
            <AppButton
              name={messages['common.filter']}
              leftIcon={<FilterOutlined />}
              onClick={() => setIsFilterOpen(true)}
            />
          </div>
          <PayslipFilterModal
            isOpen={isFilterOpen}
            toggle={() => setIsFilterOpen(false)}
            onFilter={handleFilter}
            payFrequency={payFrequency}
            appliedFilters={appliedFilters}
          />
          <AppsContent>
            <AppTableContainer
              rowKey="id"
              data={list ?? []}
              loading={loading}
              page={pagination.current}
              total={pagination.totalDocs}
              pageSize={pagination.pageSize}
              scroll={{ x: 7000, y: 700 }}
              titleEmpty={messages['payslip.empty'] ?? 'No payslips found.'}
              columns={columns}
              onChangePagination={onChangePagination}
              onChangeTable={onChangeTable}
            />
          </AppsContent>
        </div>
      </AppCard>
    </AppsContainer>
  );
};

export default PayslipListing;
